import { useQuery } from '@tanstack/react-query';
import { Ellipsis } from 'lucide-react';
import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/Sheet';
import { UserMenu } from '@/features/auth/UserMenu';
import { useCurrentUser } from '@/lib/auth';
import { settingsQuery } from '@/lib/master-data';
import { useServerEvents } from '@/lib/sse';
import { cn } from '@/lib/utils';
import { EnvBanner } from './EnvBanner';
import { ForbiddenPage } from './ForbiddenPage';
import { NAV_GROUP_LABELS, canAccessPath, isNavItemActive, navPath, visibleNavItems } from './nav';
import type { NavGroup, NavItem } from './nav';
import { SyncIndicator } from './SyncIndicator';
import { TrafficBanner } from './TrafficBanner';

function Brand() {
  return (
    <Link to="/" className="flex min-w-0 items-center gap-2.5">
      <img src="/favicon.svg" alt="" className="size-8 shrink-0 rounded-md" />
      <div className="min-w-0 leading-tight">
        <p className="truncate text-sm font-bold text-primary">Carte Lucca60</p>
        <p className="truncate text-xs text-muted-foreground">FMF S.r.l.</p>
      </div>
    </Link>
  );
}

function SidebarNav({ items }: { items: NavItem[] }) {
  const location = useLocation();
  const groups = (Object.keys(NAV_GROUP_LABELS) as NavGroup[]).filter((g) =>
    items.some((i) => i.group === g),
  );
  return (
    <nav aria-label="Navigazione principale" className="flex flex-col gap-5">
      {groups.map((group) => (
        <div key={group} className="flex flex-col gap-1">
          <p className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {NAV_GROUP_LABELS[group]}
          </p>
          {items
            .filter((i) => i.group === group)
            .map((item) => {
              const active = isNavItemActive(location.pathname, item);
              return (
                <Link
                  key={item.key}
                  to={navPath(item, location)}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'flex h-11 items-center gap-3 rounded-md px-3 text-sm font-medium text-foreground hover:bg-accent',
                    active && 'bg-primary text-primary-foreground hover:bg-primary',
                  )}
                >
                  <item.icon className="size-5 shrink-0" />
                  {item.label}
                </Link>
              );
            })}
        </div>
      ))}
    </nav>
  );
}

function BottomNav({ items, onMore }: { items: NavItem[]; onMore: () => void }) {
  const location = useLocation();
  const primary = items.filter((i) => i.primary).slice(0, 5);
  const others = items.filter((i) => !primary.includes(i));
  const moreActive = others.some((i) => isNavItemActive(location.pathname, i));
  const itemClass =
    'flex min-h-14 flex-col items-center justify-center gap-0.5 px-0.5 text-[11px] font-medium tracking-tight';
  return (
    <nav
      aria-label="Navigazione principale"
      data-print="hide"
      className="grid shrink-0 select-none border-t border-border bg-background pb-[env(safe-area-inset-bottom)] lg:hidden"
      style={{
        gridTemplateColumns: `repeat(${primary.length + (others.length ? 1 : 0)}, minmax(0, 1fr))`,
      }}
    >
      {primary.map((item) => {
        const active = isNavItemActive(location.pathname, item);
        return (
          <Link
            key={item.key}
            to={navPath(item, location)}
            aria-current={active ? 'page' : undefined}
            className={cn(itemClass, active ? 'text-primary' : 'text-muted-foreground')}
          >
            <item.icon className={cn('size-6', active && 'stroke-[2.5]')} />
            <span className="max-w-full truncate">{item.label}</span>
          </Link>
        );
      })}
      {others.length ? (
        <button
          type="button"
          onClick={onMore}
          className={cn(itemClass, moreActive ? 'text-primary' : 'text-muted-foreground')}
        >
          <Ellipsis className="size-6" />
          <span>Altro</span>
        </button>
      ) : null}
    </nav>
  );
}

function MoreSheet({
  items,
  open,
  onOpenChange,
}: {
  items: NavItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const location = useLocation();
  const primary = items.filter((i) => i.primary).slice(0, 5);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="lg:hidden">
        <SheetHeader>
          <SheetTitle>Altre sezioni</SheetTitle>
          <SheetDescription className="sr-only">
            Sezioni non presenti nella barra inferiore
          </SheetDescription>
        </SheetHeader>
        {/* one column on narrow phones: "Sincronizzazione" does not fit half of a 375 px screen */}
        <div className="grid grid-cols-1 gap-2 min-[420px]:grid-cols-2 sm:grid-cols-3">
          {items
            .filter((i) => !primary.includes(i))
            .map((item) => (
              <Link
                key={item.key}
                to={navPath(item, location)}
                onClick={() => onOpenChange(false)}
                className={cn(
                  'flex min-h-12 items-center gap-3 rounded-lg border border-border px-3 text-sm font-medium',
                  isNavItemActive(location.pathname, item) &&
                    'border-primary bg-secondary text-primary',
                )}
              >
                <item.icon className="size-5 shrink-0" />
                <span className="min-w-0">{item.label}</span>
              </Link>
            ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Responsive application shell (docs/08 §7): sidebar on desktop (≥ 1024 px), bottom bar with the
 * 5 main sections + "Altro" on tablet and phone; environment and traffic-light banners always on
 * top; navigation filtered by role; pages of other roles answer "Accesso non consentito". On a
 * detail opened from a list (`?torna=`) the menu entry of that list goes back to it, filters
 * included (`navPath(item, location)`); the active entry follows the pathname.
 */
export function AppShell() {
  const user = useCurrentUser();
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  useServerEvents();
  // `readonlyShowsQuantities` gates the archive of `readonly` (public settings, SSE `settings`)
  const { data: settings } = useQuery({ ...settingsQuery, enabled: user.role === 'readonly' });
  const readonlyShowsQuantities = settings?.readonlyShowsQuantities;
  const items = visibleNavItems(user.role, readonlyShowsQuantities);
  const allowed = canAccessPath(user.role, pathname, readonlyShowsQuantities);

  return (
    // data-print (index.css): window.print() of a page prints only its content
    <div
      data-print="flow"
      className="relative flex h-dvh flex-col overflow-hidden pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] pt-[env(safe-area-inset-top)]"
    >
      <div data-print="hide" className="contents">
        <EnvBanner />
        <TrafficBanner />
      </div>
      <div className="flex min-h-0 flex-1">
        <aside
          data-print="hide"
          className="hidden w-64 shrink-0 flex-col gap-6 overflow-y-auto overscroll-contain border-r border-border bg-sidebar px-3 py-5 lg:flex"
        >
          <div className="px-3">
            <Brand />
          </div>
          <SidebarNav items={items} />
          <div className="mt-auto flex flex-col gap-2 px-1">
            <SyncIndicator className="self-start" />
            <UserMenu className="w-full" />
          </div>
        </aside>
        <div className="flex min-w-0 flex-1 flex-col">
          <header
            data-print="hide"
            className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-border px-4 lg:hidden"
          >
            <Brand />
            <div className="flex items-center gap-2">
              <SyncIndicator compact />
              <UserMenu compact />
            </div>
          </header>
          <main
            data-print="flow"
            className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain"
          >
            <div className="mx-auto w-full max-w-7xl px-4 py-5 sm:px-6 lg:px-8 lg:py-8">
              {allowed ? <Outlet /> : <ForbiddenPage />}
            </div>
          </main>
          <BottomNav items={items} onMore={() => setMoreOpen(true)} />
        </div>
      </div>
      <MoreSheet items={items} open={moreOpen} onOpenChange={setMoreOpen} />
    </div>
  );
}
